import React, { useEffect, useMemo, useRef, useState } from 'react';

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface FilterSelectProps {
  /** Currently selected value; '' means "all" (no filter). */
  value: string;
  onChange: (value: string) => void;
  options: FilterOption[];
  /** Label for the empty value, e.g. "All cities". */
  allLabel: string;
  /** Show the search input inside the menu. */
  searchable?: boolean;
  searchPlaceholder?: string;
  ariaLabel: string;
}

/**
 * Custom filter dropdown for admin list headers, styled to match the native
 * `.admin-events__status` select. Optionally embeds a search input to narrow
 * long option lists (e.g. cities).
 */
export const FilterSelect = ({
  value,
  onChange,
  options,
  allLabel,
  searchable = false,
  searchPlaceholder = 'Search…',
  ariaLabel,
}: FilterSelectProps): React.JSX.Element => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  // "All" is only offered when not searching — a query means the user is
  // looking for a specific option.
  const showAll = !query.trim();
  // Flat list the keyboard navigation walks over: index 0 is "All" when shown.
  const itemCount = filtered.length + (showAll ? 1 : 0);

  const close = (): void => {
    setOpen(false);
    setQuery('');
    setActiveIndex(-1);
  };

  const select = (next: string): void => {
    onChange(next);
    close();
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
  }, [open, searchable]);

  // Keep the keyboard-highlighted row visible while arrowing through the list.
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.children[activeIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!itemCount) return;
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((i) => (i + delta + itemCount) % itemCount);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex < 0) return;
      if (showAll && activeIndex === 0) select('');
      else select(filtered[activeIndex - (showAll ? 1 : 0)].value);
    }
  };

  const selected = options.find((o) => o.value === value);
  const buttonLabel = value ? (selected?.label ?? value) : allLabel;

  return (
    <div className="filter-select" ref={rootRef} onKeyDown={handleKeyDown}>
      <button
        type="button"
        className="admin-events__status filter-select__btn"
        onClick={() => (open ? close() : setOpen(true))}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        {buttonLabel}
      </button>

      {open && (
        <div className="filter-select__menu">
          {searchable && (
            <input
              ref={searchRef}
              type="text"
              className="filter-select__search"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(-1);
              }}
              aria-label={searchPlaceholder}
            />
          )}
          <div className="filter-select__list" role="listbox" ref={listRef}>
            {showAll && (
              <button
                type="button"
                role="option"
                aria-selected={!value}
                className={[
                  'filter-select__option',
                  !value ? 'filter-select__option--selected' : '',
                  activeIndex === 0 ? 'filter-select__option--active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => select('')}
              >
                {allLabel}
              </button>
            )}
            {filtered.map((opt, i) => {
              const index = i + (showAll ? 1 : 0);
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={opt.value === value}
                  className={[
                    'filter-select__option',
                    opt.value === value ? 'filter-select__option--selected' : '',
                    activeIndex === index ? 'filter-select__option--active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => select(opt.value)}
                >
                  <span className="filter-select__option-label">
                    {opt.label}
                  </span>
                  {opt.count != null && (
                    <span className="filter-select__option-count">
                      {opt.count}
                    </span>
                  )}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="filter-select__empty">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
